/*
  CODEX CUSTOMIZATION START
  File: custom-wishlist-hydration.js
  Purpose:
  - Hydrate Wishlist Power's guest/customer wishlist outside the wishlist page.
  - Update existing Wishlist Power count and heart controls without per-card requests.
  CODEX CUSTOMIZATION END
*/

(() => {
  const WISHLIST_READY_EVENT = 'ooo-wishlist:ready';
  const COUNT_SELECTOR = 'ooo-wl-wishlist-count';
  const BUTTON_SELECTOR = 'ooo-wl-product-card-button, ooo-wl-wishlist-button';
  const API_WAIT_TIMEOUT = 4000;
  const HYDRATION_KEY = '__codexWishlistHydrationStarted';

  function getWishlistApi() {
    const wishlistApi = window.Maestrooo?.wishlist;

    return wishlistApi && typeof wishlistApi.getContent === 'function' ? wishlistApi : null;
  }

  function waitForWishlistApi() {
    const api = getWishlistApi();

    if (api) {
      return Promise.resolve(api);
    }

    return new Promise((resolve) => {
      let timeoutId;

      const complete = () => {
        window.clearTimeout(timeoutId);
        document.removeEventListener(WISHLIST_READY_EVENT, complete);
        resolve(getWishlistApi());
      };

      document.addEventListener(WISHLIST_READY_EVENT, complete, { once: true });
      timeoutId = window.setTimeout(complete, API_WAIT_TIMEOUT);
    });
  }

  function refreshWishlistCount() {
    document.querySelectorAll(COUNT_SELECTOR).forEach((countElement) => {
      // Wishlist Power refreshes its own count when an existing count attribute is removed.
      // The API result is now cached, so this is a local read rather than another request.
      if (!countElement.hasAttribute('count')) {
        countElement.setAttribute('count', '0');
      }

      countElement.removeAttribute('count');
    });
  }

  function getWishlistButtonControl(buttonElement) {
    return buttonElement.wishlistButton
      || buttonElement.button
      || buttonElement.shadowRoot?.querySelector('button')
      || buttonElement.querySelector('button');
  }

  function getWishlistButtonHandle(buttonElement) {
    if (typeof buttonElement.handle === 'string' && buttonElement.handle !== '') {
      return buttonElement.handle;
    }

    return buttonElement.getAttribute('handle');
  }

  function refreshWishlistButtons(products) {
    const savedHandles = new Set(
      products
        .map((product) => product?.handle)
        .filter((handle) => typeof handle === 'string' && handle !== '')
    );
    const translations = window.ooo?.wishlist?.translations || {};

    document.querySelectorAll(BUTTON_SELECTOR).forEach((buttonElement) => {
      const handle = getWishlistButtonHandle(buttonElement);
      const control = getWishlistButtonControl(buttonElement);

      if (!handle || !control) {
        return;
      }

      const isSaved = savedHandles.has(handle);

      control.setAttribute('aria-checked', String(isSaved));
      control.removeAttribute('loading');
      buttonElement.removeAttribute('loading');

      if (isSaved && translations.remove_from_wishlist) {
        control.setAttribute('aria-label', translations.remove_from_wishlist);
      } else if (!isSaved && translations.add_to_wishlist) {
        control.setAttribute('aria-label', translations.add_to_wishlist);
      }
    });
  }

  async function hydrateWishlist() {
    const wishlistApi = await waitForWishlistApi();

    if (!wishlistApi) {
      return;
    }

    try {
      // `true` lets Wishlist Power use its 15-minute local cache before making a request.
      const content = await wishlistApi.getContent(true);
      const products = Array.isArray(content?.products) ? content.products : [];

      refreshWishlistCount();
      refreshWishlistButtons(products);
    } catch (error) {
      // Wishlist controls remain usable if the optional background hydration fails.
      console.debug('[Wishlist Power] Background wishlist hydration was unavailable.', error);
    }
  }

  function scheduleHydration() {
    if (window[HYDRATION_KEY]) {
      return;
    }

    window[HYDRATION_KEY] = true;

    const runWhenIdle = () => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(hydrateWishlist, { timeout: 1200 });
      } else {
        window.setTimeout(hydrateWishlist, 0);
      }
    };

    if (document.readyState === 'complete') {
      runWhenIdle();
    } else {
      window.addEventListener('load', runWhenIdle, { once: true });
    }
  }

  scheduleHydration();
})();
